<template>
    <div class="virtual-scroller" ref="container" @scroll="onScroll">
        <!-- 撑开滚动高度的占位元素 -->
        <div class="virtual-spacer" :style="{ height: totalHeight + 'px' }"></div>
        <!-- 实际渲染的可见区域 -->
        <div ref="content" class="virtual-content" :style="{ transform: `translateY(${offsetY}px)` }">
            <div v-for="(item, idx) in visibleItems" :key="itemKey(item, idx)">
                <slot :item="item" :index="startIndex + idx"></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "virtualScroller",
    data() {
        return {
            startIndex: 0,
            endIndex: 0,
            offsetY: 0,
            containerHeight: 0,
            measuredItemHeight: 0,
            lockTip: false,
            isBottomReached: false
        }
    },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        // 虚拟滚动依赖真实行高；未传时会回退到首屏首项测量
        itemHeight: {
            type: Number,
            default: 0
        },
        itemHeightGetter: {
            type: Function,
            default: null
        },
        itemKeyGetter: {
            type: Function,
            default: null
        },
        itemKeyField: {
            type: String,
            default: ''
        },
        // 上下额外渲染的缓冲条数
        buffer: {
            type: Number,
            default: 5
        },
        showReachBottomTip: {
            type: Boolean,
            default: false
        },
        // 兼容旧 API: size 属性（不再使用，保留避免报错）
        size: {
            type: Number,
            default: 30
        }
    },
    methods: {
        resolveStableItemKey(item, absoluteIndex) {
            if (this.itemKeyGetter) {
                const customKey = this.itemKeyGetter(item, absoluteIndex);
                if (customKey !== undefined && customKey !== null && customKey !== '') {
                    return String(customKey);
                }
            }
            if (this.itemKeyField && item && item[this.itemKeyField] !== undefined && item[this.itemKeyField] !== null) {
                return String(item[this.itemKeyField]);
            }
            if (item && item.key !== undefined && item.key !== null) {
                return String(item.key);
            }
            if (item && item.id !== undefined && item.id !== null) {
                return String(item.id);
            }
            return '';
        },
        findItemIndexByStableKey(itemKey) {
            if (!itemKey) {
                return -1;
            }
            for (let index = 0; index < this.items.length; index += 1) {
                if (this.resolveStableItemKey(this.items[index], index) === itemKey) {
                    return index;
                }
            }
            return -1;
        },
        captureScrollAnchor() {
            const container = this.$refs.container;
            if (!container) {
                return null;
            }
            const clientHeight = container.clientHeight || this.containerHeight || 0;
            const domScrollHeight = container.scrollHeight || this.totalHeight || 0;
            const maxScrollTop = Math.max(domScrollHeight - clientHeight, 0);
            const scrollTop = Math.max(0, Math.min(container.scrollTop, maxScrollTop));
            const atBottom = maxScrollTop > 0 && scrollTop >= maxScrollTop - 2;
            const anchor = {
                scrollTop,
                totalHeight: domScrollHeight,
                atBottom,
                key: '',
                offset: 0
            };
            if (!this.usesVariableHeight || this.items.length === 0) {
                return anchor;
            }
            const anchorIndex = Math.max(0, Math.min(this.startIndex, this.items.length - 1));
            anchor.key = this.resolveStableItemKey(this.items[anchorIndex], anchorIndex);
            anchor.offset = scrollTop - this.getItemOffset(anchorIndex);
            return anchor;
        },
        restoreScrollAnchor(anchor) {
            if (!anchor) {
                return;
            }
            const container = this.$refs.container;
            if (!container) {
                return;
            }
            const clientHeight = container.clientHeight || this.containerHeight || 0;
            const domScrollHeight = container.scrollHeight || this.totalHeight || 0;
            const maxScrollTop = Math.max(domScrollHeight - clientHeight, 0);
            if (anchor.atBottom) {
                // 分页向下追加时保持当前位置，避免“吸底”导致滚动体感停滞
                container.scrollTop = Math.max(0, Math.min(anchor.scrollTop, maxScrollTop));
                return;
            }
            if (!this.usesVariableHeight) {
                container.scrollTop = Math.max(0, Math.min(anchor.scrollTop, maxScrollTop));
                return;
            }
            let nextScrollTop = anchor.scrollTop;
            if (anchor.key) {
                const nextIndex = this.findItemIndexByStableKey(anchor.key);
                if (nextIndex >= 0) {
                    nextScrollTop = this.getItemOffset(nextIndex) + (Number(anchor.offset) || 0);
                } else {
                    nextScrollTop = anchor.scrollTop + (domScrollHeight - (Number(anchor.totalHeight) || 0));
                }
            }
            container.scrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop));
        },
        buildScrollState(scrollTop, totalHeight, reachedBottom) {
            return {
                scrollTop,
                clientHeight: this.containerHeight,
                scrollHeight: totalHeight,
                reachedBottom,
                startIndex: this.startIndex,
                endIndex: this.endIndex
            };
        },
        init() {
            this.syncItemHeight();
            this.updateVisibleRange();
        },
        itemKey(item, idx) {
            const stableKey = this.resolveStableItemKey(item, this.startIndex + idx);
            if (stableKey) {
                return stableKey;
            }
            if (item && item.key !== undefined && item.key !== null) {
                return item.key;
            }
            if (item && item.id !== undefined && item.id !== null) {
                return item.id;
            }
            return this.startIndex + idx;
        },
        getItemHeightByIndex(index) {
            if (this.usesVariableHeight) {
                return this.itemHeights[index] || this.defaultItemHeight;
            }
            return this.resolvedItemHeight;
        },
        getItemOffset(index) {
            if (this.usesVariableHeight) {
                return this.itemOffsets[index] || 0;
            }
            return index * this.resolvedItemHeight;
        },
        findIndexForOffset(offset) {
            if (!this.usesVariableHeight) {
                if (this.resolvedItemHeight <= 0) {
                    return 0;
                }
                return Math.floor(offset / this.resolvedItemHeight);
            }
            const totalItems = this.items.length;
            if (totalItems === 0) {
                return 0;
            }
            const safeOffset = Math.max(0, offset);
            let low = 0;
            let high = totalItems;
            while (low < high) {
                const mid = Math.floor((low + high + 1) / 2);
                if (this.itemOffsets[mid] <= safeOffset) {
                    low = mid;
                } else {
                    high = mid - 1;
                }
            }
            return Math.min(low, totalItems - 1);
        },
        scrollToIndex(index, options = {}) {
            const container = this.$refs.container;
            const safeIndex = Math.max(0, Math.min(index, this.items.length - 1));
            const itemHeight = this.getItemHeightByIndex(safeIndex);
            if (!container || itemHeight <= 0 || this.items.length === 0) return;
            const align = options.align || 'start';
            let scrollTop = this.getItemOffset(safeIndex);
            if (align === 'center') {
                scrollTop -= Math.max((container.clientHeight - itemHeight) / 2, 0);
            } else if (align === 'end') {
                scrollTop -= Math.max(container.clientHeight - itemHeight, 0);
            }
            const maxScrollTop = Math.max(this.totalHeight - container.clientHeight, 0);
            container.scrollTop = Math.max(0, Math.min(scrollTop, maxScrollTop));
            this.updateVisibleRange();
        },
        onScroll() {
            const wasBottomReached = this.isBottomReached;
            const state = this.updateVisibleRange({ fromScroll: true });
            if (state) {
                this.$emit('scroll', state);
                if (state.reachedBottom && !wasBottomReached) {
                    this.$emit('reach-bottom', state);
                }
            }
        },
        syncItemHeight() {
            if (this.itemHeight > 0 || this.usesVariableHeight) {
                this.measuredItemHeight = 0;
                return;
            }
            const content = this.$refs.content;
            if (!content) return;

            const firstItem = content.firstElementChild;
            if (!firstItem) return;

            const height = Math.ceil(firstItem.getBoundingClientRect().height);
            if (height > 0) {
                this.measuredItemHeight = height;
            }
        },
        updateVisibleRange(options = {}) {
            const container = this.$refs.container;
            const itemHeight = this.resolvedItemHeight;
            if (!container || (!this.usesVariableHeight && itemHeight <= 0)) return;

            this.containerHeight = container.clientHeight;
            const totalHeight = this.totalHeight;
            const maxScrollTop = Math.max(totalHeight - this.containerHeight, 0);
            const scrollTop = Math.min(container.scrollTop, maxScrollTop);
            if (container.scrollTop !== scrollTop) {
                container.scrollTop = scrollTop;
            }

            let start = 0;
            let end = 0;
            if (this.usesVariableHeight) {
                if (this.items.length > 0) {
                    const baseStart = this.findIndexForOffset(scrollTop);
                    const bottomOffset = Math.min(scrollTop + this.containerHeight, totalHeight);
                    const baseEnd = bottomOffset > 0
                        ? this.findIndexForOffset(Math.max(bottomOffset - 1, 0)) + 1
                        : 0;
                    start = Math.max(baseStart - this.buffer, 0);
                    end = Math.min(baseEnd + this.buffer, this.items.length);
                    end = Math.max(end, start);
                }
            } else {
                const visibleCount = Math.max(Math.ceil(this.containerHeight / itemHeight), 1);

                // 计算可见范围（含缓冲区）
                start = Math.floor(scrollTop / itemHeight) - this.buffer;
                const maxStart = Math.max(this.items.length - visibleCount, 0);
                start = Math.max(0, Math.min(start, maxStart));
                end = start + visibleCount + this.buffer * 2;
                end = Math.min(end, this.items.length);
            }

            this.startIndex = start;
            this.endIndex = end;
            this.offsetY = this.getItemOffset(start);

            const isBottomReached = totalHeight > this.containerHeight
                && scrollTop + this.containerHeight >= totalHeight - 10;

            // 仅在用户真实滚动触底时提示，避免初始化和筛选时误提示
            if (options.fromScroll && isBottomReached && !this.isBottomReached) {
                this.showTip();
            }
            this.isBottomReached = isBottomReached;
            return this.buildScrollState(scrollTop, totalHeight, isBottomReached);
        },
        showTip() {
            if (!this.showReachBottomTip) {
                return;
            }
            if (!this.lockTip && this.items.length > 0) {
                this.$message.success("已滚动到底部");
                this.lockTip = true;
                setTimeout(() => {
                    this.lockTip = false;
                }, 3000);
            }
        }
    },
    computed: {
        usesVariableHeight() {
            return typeof this.itemHeightGetter === 'function';
        },
        defaultItemHeight() {
            if (this.itemHeight > 0) {
                return this.itemHeight;
            }
            if (this.measuredItemHeight > 0) {
                return this.measuredItemHeight;
            }
            return 1;
        },
        itemHeights() {
            if (!this.usesVariableHeight) {
                return [];
            }
            return this.items.map((item, index) => {
                const resolvedHeight = Number(this.itemHeightGetter(item, index));
                if (Number.isFinite(resolvedHeight) && resolvedHeight > 0) {
                    return Math.ceil(resolvedHeight);
                }
                return this.defaultItemHeight;
            });
        },
        itemOffsets() {
            if (!this.usesVariableHeight) {
                return [];
            }
            const offsets = [0];
            this.itemHeights.forEach((height) => {
                offsets.push(offsets[offsets.length - 1] + height);
            });
            return offsets;
        },
        resolvedItemHeight() {
            if (this.itemHeight > 0) {
                return this.itemHeight;
            }
            return this.measuredItemHeight;
        },
        totalHeight() {
            if (this.usesVariableHeight) {
                return this.itemOffsets[this.items.length] || 0;
            }
            if (this.resolvedItemHeight <= 0) {
                return 0;
            }
            return this.items.length * this.resolvedItemHeight;
        },
        visibleItems() {
            if (this.usesVariableHeight) {
                if (this.endIndex > this.startIndex) {
                    return this.items.slice(this.startIndex, this.endIndex);
                }
                const initCount = Math.max(this.buffer * 2 + 1, 10);
                return this.items.slice(0, initCount);
            }
            if (this.resolvedItemHeight <= 0) {
                const initCount = Math.max(this.buffer * 2 + 1, 10);
                return this.items.slice(0, initCount);
            }
            return this.items.slice(this.startIndex, this.endIndex);
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.syncItemHeight();
            this.updateVisibleRange();
        });
    },
    watch: {
        items() {
            const anchor = this.captureScrollAnchor();
            this.$nextTick(() => {
                this.syncItemHeight();
                this.updateVisibleRange();
                this.restoreScrollAnchor(anchor);
                this.updateVisibleRange();
            });
        },
        itemHeight() {
            this.$nextTick(() => {
                this.syncItemHeight();
                this.updateVisibleRange();
            });
        }
    }
}
</script>

<style scoped>
.virtual-scroller {
    overflow-y: auto;
    overscroll-behavior: contain;
    overflow-anchor: none;
    scrollbar-gutter: stable;
    position: relative;
    height: 100%;
    background: inherit;
}
.virtual-spacer {
    width: 100%;
    background: inherit;
}
.virtual-content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: inherit;
}
</style>
